import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { BuildSchedule } from '@/services/tenantConfig.service';

// Common timezone options
const TIMEZONE_OPTIONS = [
  { label: 'UTC', value: 'UTC' },
  { label: 'US/Eastern', value: 'America/New_York' },
  { label: 'US/Central', value: 'America/Chicago' },
  { label: 'US/Mountain', value: 'America/Denver' },
  { label: 'US/Pacific', value: 'America/Los_Angeles' },
  { label: 'Europe/London', value: 'Europe/London' },
  { label: 'Europe/Paris', value: 'Europe/Paris' },
  { label: 'Europe/Berlin', value: 'Europe/Berlin' },
  { label: 'Asia/Tokyo', value: 'Asia/Tokyo' },
  { label: 'Asia/Shanghai', value: 'Asia/Shanghai' },
  { label: 'Asia/Singapore', value: 'Asia/Singapore' },
  { label: 'Australia/Sydney', value: 'Australia/Sydney' },
];

// Cron presets for easier selection
const CRON_PRESETS = [
  { label: 'Every day at 2am', value: '0 2 * * *' },
  { label: 'Every Monday at 2am', value: '0 2 * * 1' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Every day at midnight', value: '0 0 * * *' },
  { label: 'Weekdays at 6am', value: '0 6 * * 1-5' },
];

export default function DbtBuildsTab() {
  const queryClient = useQueryClient();
  const [tenantConfigId, setTenantConfigId] = useState('');

  // Build Schedule State
  const [cronExpression, setCronExpression] = useState('0 2 * * 1');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(false);

  // Variables State
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [newVariableName, setNewVariableName] = useState('');

  // Loading States
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingVariables, setSavingVariables] = useState(false);

  // Fetch tenant registration data
  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Initialize form with fetched data
  useEffect(() => {
    if (registrationData) {
      const { tenant_config } = registrationData;
      setTenantConfigId(tenant_config.id);

      if (tenant_config.build_schedule) {
        setCronExpression(
          tenant_config.build_schedule.cron_expression || '0 2 * * 1'
        );
        setTimezone(
          tenant_config.build_schedule.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        setScheduleEnabled(tenant_config.build_schedule.enabled || false);
      }

      if (tenant_config.dbt_variables) {
        setVariables(tenant_config.dbt_variables);
      }
    }
  }, [registrationData]);

  const saveBuildSchedule = useCallback(async () => {
    if (!tenantConfigId) return;

    setSavingSchedule(true);
    try {
      const schedule: BuildSchedule = {
        cron_expression: cronExpression,
        timezone: timezone,
        enabled: scheduleEnabled,
      };

      await TenantConfigService.getInstance().saveBuildSchedule(
        tenantConfigId,
        schedule
      );

      showToast.success('Build schedule saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to save build schedule', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingSchedule(false);
    }
  }, [tenantConfigId, cronExpression, timezone, scheduleEnabled, queryClient]);

  const addVariable = useCallback(() => {
    if (!newVariableName.trim()) return;

    const varName = newVariableName.trim().toUpperCase().replace(/\s+/g, '_');
    setVariables((prev) => ({ ...prev, [varName]: '' }));
    setNewVariableName('');
  }, [newVariableName]);

  const updateVariable = useCallback((key: string, value: string) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  }, []);

  const deleteVariable = useCallback((key: string) => {
    setVariables((prev) => {
      const newVars = { ...prev };
      delete newVars[key];
      return newVars;
    });
  }, []);

  const saveVariables = useCallback(async () => {
    if (!tenantConfigId) return;

    setSavingVariables(true);
    try {
      await TenantConfigService.getInstance().saveVariables(
        tenantConfigId,
        variables
      );
      showToast.success('Variables saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to save variables', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingVariables(false);
    }
  }, [tenantConfigId, variables, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Build Schedule Card */}
      <Card>
        <CardHeader>
          <CardTitle>Build Schedule</CardTitle>
          <CardDescription>
            Configure when your DBT models are automatically built. The schedule
            uses cron expressions to define the timing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Schedule Enable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Schedule Status</Label>
              <p className="text-sm text-muted-foreground">
                {scheduleEnabled
                  ? 'Builds will run automatically'
                  : 'Automatic builds are disabled'}
              </p>
            </div>
            <Switch
              checked={scheduleEnabled}
              onCheckedChange={setScheduleEnabled}
            />
          </div>

          {/* Cron Expression */}
          <div className="space-y-2">
            <Label>Build Schedule (Cron Expression)</Label>
            <div className="flex gap-2">
              <Input
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                placeholder="0 2 * * 1"
                disabled={!scheduleEnabled}
                className="font-mono"
              />
              <Select
                value=""
                onValueChange={(value) => setCronExpression(value)}
                disabled={!scheduleEnabled}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Presets..." />
                </SelectTrigger>
                <SelectContent>
                  {CRON_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              Format: minute hour day-of-month month day-of-week
            </p>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select
              value={timezone}
              onValueChange={setTimezone}
              disabled={!scheduleEnabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Save Button */}
          <Button onClick={saveBuildSchedule} disabled={savingSchedule}>
            {savingSchedule ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Schedule'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Variables Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>DBT Variables</CardTitle>
              <CardDescription>
                Set DBT variables that will be available anywhere you use custom
                SQL (including custom transforms). Use them like{' '}
                <code className="bg-muted px-1 py-0.5 rounded text-sm">
                  {"{{var('VAR_NAME')}}"}
                </code>
              </CardDescription>
            </div>
            <Button onClick={saveVariables} disabled={savingVariables}>
              {savingVariables ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Variables'
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Variable */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter variable name"
              value={newVariableName}
              onChange={(e) => setNewVariableName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addVariable();
                }
              }}
              className="max-w-[300px]"
            />
            <Button variant="secondary" onClick={addVariable}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Variables Table */}
          {Object.keys(variables).length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Variable</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(variables).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell className="font-mono">{key}</TableCell>
                    <TableCell>
                      <Input
                        value={value}
                        onChange={(e) => updateVariable(key, e.target.value)}
                        placeholder="Enter value"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteVariable(key)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No variables defined. Add a variable above to get started.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
